import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetSimpleStepItems } from "../../hooks/simple-steps/useGetSimpleStepItems";
import { useDeleteSimpleStepItem } from "../../hooks/simple-steps/useDeleteSimpleStepItem";
import { useDeleteAllSimpleStepItems } from "../../hooks/simple-steps/useDeleteAllSimpleStepItems";
import { useRestoreAllSimpleStepItems } from "../../hooks/simple-steps/useRestoreAllSimpleStepItems";
import { useForceDeleteAllSimpleStepItems } from "../../hooks/simple-steps/useForceDeleteAllSimpleStepItems";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import SimpleStepItemsTableRow from "./Table/SimpleStepItemsTableRow";
import { SimpleStepItemsTableColumns } from "./Table/SimpleStepItemsTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const SimpleStepItemsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("viewAny-simple-step-item"),
            create: has("create-simple-step-item"),
            edit: has("update-simple-step-item"),
            delete: has("delete-simple-step-item"),
        }),
        [has]
    );

    const navigate = useNavigate();

    const [trashedFilter, setTrashedFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    const { deleteItemMutate, isPending: isDelete } = useDeleteSimpleStepItem();
    const { deleteAllItemsMutate, isPending: isDeleteAll } = useDeleteAllSimpleStepItems();
    const { restoreAllItemsMutate, isPending: isRestoreAll } = useRestoreAllSimpleStepItems();
    const { forceDeleteAllItemsMutate, isPending: isForceDeleteAll } = useForceDeleteAllSimpleStepItems();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handleBulkRestore,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/simple-steps",
        deleteSingle: deleteItemMutate,
        deleteAll: deleteAllItemsMutate,
        restoreSingle: (id, options) => restoreAllItemsMutate({ items: [id] }, options),
        restoreAll: restoreAllItemsMutate,
        forceDeleteSingle: (id, options) => forceDeleteAllItemsMutate({ items: [id] }, options),
        forceDeleteAll: forceDeleteAllItemsMutate,
        isArchive: trashedFilter === "only",
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            search: search || undefined,
            trashed: trashedFilter === "only" ? "only" : undefined,
            is_active: statusFilter || undefined,
            // sort_by: 'sort_order',
            // sort_order: order,
        }),
        [page, perPage, search, trashedFilter, statusFilter]
    );

    const { isLoading, error, items, meta } = useGetSimpleStepItems(params);

    const filters = useMemo(() => [
        {
            key: "is_active",
            label: { ar: "الحالة", en: "Status" },
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
                { value: "", label: { ar: "الكل", en: "All" } },
                { value: "1", label: { ar: "نشط", en: "Active" } },
                { value: "0", label: { ar: "غير نشط", en: "Inactive" } },
            ],
        },
    ], [statusFilter]);

    const columns = useMemo(() => SimpleStepItemsTableColumns(isArabic), [isArabic]);

    return (
        <TableLayout
            title={{ ar: "الخطوات البسيطة", en: "Simple Steps" }}
            columns={columns}
            items={items}
            meta={meta}
            isLoading={isLoading}
            error={error}
            perms={perms}
            filters={filters}
            selectedIds={selectedIds}
            search={search}
            onSearchChange={setSearch}
            onSelectAllClick={(e) => handleSelectAllClick(e, items)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onBulkDelete={handleBulkDelete}
            onBulkRestore={handleBulkRestore}
            isBulkDeleting={isDeleteAll || isForceDeleteAll}
            isBulkRestoring={isRestoreAll}
            isArchive={trashedFilter === "only"}
            onAddClick={() => navigate("/simple-steps/create")}
            addButtonLabel={{ ar: "إضافة خطوة", en: "Add Step" }}
            page={page}
            perPage={perPage}
            order={order}
            onRequestSort={handleRequestSort}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            onMenuRestore={handleMenuRestore}
            onArchiveToggle={() => setTrashedFilter(prev => prev === "only" ? "" : "only")}
            isDeletingSingle={isDelete || isForceDeleteAll}
            renderRow={(params) => (
                <SimpleStepItemsTableRow
                    {...params}
                    key={params.item.id}
                    item={params.item}
                    perms={perms}
                    onMenuOpen={handleMenuOpen}
                />
            )}
        />
    );
};

export default SimpleStepItemsTable;
